import { useEffect, useMemo, useReducer, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, Save } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/hooks/useSession';
import { useAuthoringNavigationGuard } from '@/features/qms/hooks/useAuthoringNavigationGuard';
import { CompanyLogoField } from '@/features/crm/components/CompanyLogoField';
import { BudgetsSection } from '../components/editor/BudgetsSection';
import { ContextSection } from '../components/editor/ContextSection';
import { ConvertToQuoteDialog } from '../components/editor/ConvertToQuoteDialog';
import { DeckPanel } from '../components/editor/DeckPanel';
import { EstimateActionsBar } from '../components/editor/EstimateActionsBar';
import { EquipmentSection } from '../components/editor/EquipmentSection';
import { LiveTotalsPanel } from '../components/editor/LiveTotalsPanel';
import { ProposalSection } from '../components/editor/ProposalSection';
import { SiteSection } from '../components/editor/SiteSection';
import { EstimateStatusBadge } from '../components/EstimateStatusBadge';
import { useDeleteEstimate, useEstimate, useEstimateCommand, useSaveEstimate, type EstimateCommand } from '../hooks/useEstimates';
import { availableActions } from '../lib/actions';
import { draftFromEstimate, editorReducer, isDirty, toSaveBody } from '../lib/editorState';
import { errorBody, errorCode, errorMessage, fieldErrorMap } from '../lib/errors';
import { estimateLabel } from '../lib/format';
import { priceDraft, type LivePricing } from '../lib/livePricing';
import type { Estimate, EstimateDetail } from '../types';

/** /crm/estimates/:estimateId — the full-page estimate editor (spec § V2 user interface). */
export function EstimateEditorPage() {
  const { estimateId } = useParams<{ estimateId: string }>();
  const { t } = useTranslation('crm');
  const { data, isLoading, isError, error, refetch } = useEstimate(estimateId);
  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertDescription>{t('estimates.editor.loadFailed')} {error ? errorMessage(error) : ''}</AlertDescription>
        </Alert>
      </div>
    );
  }
  return <EstimateEditor key={data.estimate.id} detail={data} onReload={async () => (await refetch()).data} />;
}

const NO_PRICING: LivePricing = { result: null, errors: [] };

const DONE_TOAST: Record<Exclude<EstimateCommand['action'], 'revise'>, string> = {
  'request-approval': 'estimates.toast.approvalRequested',
  approve: 'estimates.toast.approved',
  reject: 'estimates.toast.rejected',
  'mark-presented': 'estimates.toast.presented',
  'mark-lost': 'estimates.toast.lost',
  reprice: 'estimates.toast.repriced',
};

function EstimateEditor({ detail, onReload }: { detail: EstimateDetail; onReload: () => Promise<EstimateDetail | undefined> }) {
  const { t } = useTranslation('crm');
  const { user } = useSession();
  const { estimate, priceBook, company } = detail;
  const [baseline, setBaseline] = useState(estimate);
  const [draft, dispatch] = useReducer(editorReducer, estimate, draftFromEstimate);
  const [saveError, setSaveError] = useState<unknown>(null);
  const save = useSaveEstimate(estimate.id);
  const navigate = useNavigate();
  const command = useEstimateCommand(estimate.id);
  const remove = useDeleteEstimate();
  const [convertOpen, setConvertOpen] = useState(false);
  const [repriceError, setRepriceError] = useState<unknown>(null);

  const dirty = isDirty(draft, baseline);
  const actions = availableActions(estimate, { roles: user?.postgres_roles ?? [], userId: user?.pg_id ?? null });
  const readOnly = !actions.has('save');
  const live = useMemo(
    () => (priceBook ? priceDraft(draft.inputs, priceBook, estimate.offerType) : NO_PRICING),
    [draft.inputs, priceBook, estimate.offerType],
  );
  const errors = useMemo(
    () => ({ ...Object.fromEntries(live.errors.map((e) => [e.path, e.message])), ...fieldErrorMap(saveError) }),
    [live.errors, saveError],
  );

  useAuthoringNavigationGuard(dirty, t('estimates.editor.leaveConfirm'));

  // A newer server copy (an action on this page, or a save in another tab) replaces the draft unless the user has
  // unsaved edits: those stay, and saving them reports STALE.
  useEffect(() => {
    if (Date.parse(estimate.updatedAt) <= Date.parse(baseline.updatedAt) || isDirty(draft, baseline)) return;
    setBaseline(estimate);
    dispatch({ type: 'reset', draft: draftFromEstimate(estimate) });
  }, [estimate, baseline, draft]);

  const adopt = (next: Estimate) => {
    setBaseline(next);
    dispatch({ type: 'reset', draft: draftFromEstimate(next) });
  };

  const onSave = async () => {
    setSaveError(null);
    try {
      adopt(await save.mutateAsync(toSaveBody(draft, baseline.updatedAt)));
      toast.success(t('estimates.toast.saved'));
    } catch (e) {
      setSaveError(e);
      if (errorCode(e) !== 'STALE') toast.error(t('estimates.toast.saveFailed'), { description: errorMessage(e) });
    }
  };

  const reload = async () => {
    const fresh = await onReload();
    if (!fresh) return;
    setSaveError(null);
    adopt(fresh.estimate);
  };

  const runCommand = async (cmd: EstimateCommand): Promise<boolean> => {
    try {
      const result = await command.mutateAsync(cmd);
      if (cmd.action === 'reprice') setRepriceError(null);
      if (cmd.action === 'revise') {
        toast.success(t('estimates.toast.revised', { label: estimateLabel(result) }));
        navigate(`/crm/estimates/${result.id}`);
      } else {
        toast.success(t(DONE_TOAST[cmd.action]));
      }
      return true;
    } catch (e) {
      if (cmd.action === 'reprice') setRepriceError(e);
      toast.error(t('estimates.toast.actionFailed'), { description: errorMessage(e) });
      return false;
    }
  };

  const onDelete = async () => {
    try {
      await remove.mutateAsync(estimate.id);
      toast.success(t('estimates.toast.deleted'));
      navigate('/crm/estimates');
    } catch (e) {
      toast.error(t('estimates.toast.actionFailed'), { description: errorMessage(e) });
    }
  };

  const sectionProps = { draft, dispatch, readOnly, errors };
  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/crm/estimates"><ArrowLeft className="h-4 w-4 mr-1" />{t('estimates.editor.back')}</Link>
        </Button>
        <h1 className="text-xl font-semibold">{estimateLabel(estimate)}</h1>
        <EstimateStatusBadge status={estimate.status} />
        <Link to={`/crm/opportunities/${estimate.opportunityId}`} className="text-sm text-primary hover:underline">
          {t('estimates.editor.opportunity')}
        </Link>
        {!readOnly && (
          <Button className="ml-auto" onClick={() => void onSave()} disabled={!dirty || save.isPending}>
            <Save className="h-4 w-4 mr-1" /> {save.isPending ? t('estimates.editor.saving') : t('estimates.editor.save')}
          </Button>
        )}
      </div>
      <EstimateActionsBar
        estimate={estimate}
        actions={actions}
        dirty={dirty}
        pending={command.isPending || remove.isPending}
        onCommand={runCommand}
        onDelete={() => void onDelete()}
        onConvert={() => setConvertOpen(true)}
      />
      {detail.stalePriceBook && estimate.status === 'draft' && (
        <Alert>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>{t('estimates.editor.newPriceBook', { version: detail.latestPriceBookVersion ?? '' })}</span>
            {actions.has('reprice') && (
              <Button size="sm" variant="outline" disabled={dirty || command.isPending} onClick={() => void runCommand({ action: 'reprice' })}>
                {t('estimates.actions.reprice')}
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}
      {errorCode(repriceError) === 'REPRICE_CONFLICT' && (
        <Alert variant="destructive">
          <AlertDescription>
            <p>{errorMessage(repriceError)}</p>
            <ul className="list-disc pl-4 text-xs">
              {(errorBody(repriceError).fields ?? []).map((f) => <li key={f.path}>{f.path}: {f.message}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      {estimate.status === 'draft' && estimate.rejectionComment && (
        <Alert variant="destructive">
          <AlertDescription>{t('estimates.editor.rejected', { comment: estimate.rejectionComment })}</AlertDescription>
        </Alert>
      )}
      {estimate.quoteId && (
        <Alert>
          <AlertDescription>
            {t('estimates.editor.convertedTo')}{' '}
            <Link to={`/erp/quotes?id=${estimate.quoteId}`} className="text-primary hover:underline">{t('estimates.editor.openQuote')}</Link>
          </AlertDescription>
        </Alert>
      )}
      {estimate.status === 'superseded' && (
        <Alert><AlertDescription>{t('estimates.editor.superseded')}</AlertDescription></Alert>
      )}
      {errorCode(saveError) === 'STALE' && (
        <Alert variant="destructive">
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>{errorMessage(saveError)}</span>
            <Button size="sm" variant="outline" onClick={() => void reload()}>{t('estimates.editor.reload')}</Button>
          </AlertDescription>
        </Alert>
      )}
      {dirty && readOnly && (
        <Alert variant="destructive">
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>{t('estimates.editor.noLongerEditable')}</span>
            <Button size="sm" variant="outline" onClick={() => void reload()}>{t('estimates.editor.reload')}</Button>
          </AlertDescription>
        </Alert>
      )}
      {!priceBook && (
        <Alert variant="destructive"><AlertDescription>{t('estimates.editor.noPriceBook')}</AlertDescription></Alert>
      )}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-4">
          <ProposalSection {...sectionProps} companyId={company?.id ?? null} companyName={company?.name ?? null} logo={company ? <CompanyLogoField companyId={company.id} logoUrl={company.logoUrl} domain={company.domain} /> : null} />
          <SiteSection {...sectionProps} />
          {priceBook && <EquipmentSection {...sectionProps} rates={priceBook.rates} live={live.result} />}
          <BudgetsSection {...sectionProps} live={live.result} />
          <ContextSection {...sectionProps} />
        </div>
        <aside className="space-y-4 self-start lg:sticky lg:top-4">
          <LiveTotalsPanel live={live} currency={draft.inputs.currency} dirty={dirty} status={estimate.status} />
          <DeckPanel estimate={estimate} canGenerate={actions.has('generate-deck')} onReload={() => void reload()} />
        </aside>
      </div>
      {convertOpen && (
        <ConvertToQuoteDialog
          open
          onOpenChange={setConvertOpen}
          estimate={estimate}
          accountId={estimate.accountId ?? company?.accountId ?? null}
        />
      )}
    </div>
  );
}
